import * as React from 'react';
import Emoji from 'components/Emoji';
// @ts-ignore
import ShortcodeEmoji from 'components/ShortcodeEmoji';
import Link from 'components/Link';
// @ts-ignore
import emojiAnnotationMap from 'emoji-annotation-to-unicode';
import matchStrings from 'utils/matchStrings';
import { replace as emojiReplace } from 'lib/emoji';
import Linkify from 'linkify-it';

const linkify = Linkify();

type RendererFn = (text: any, rendered: any, data: any) => React.ReactNode;
type EntityTypeData = {
  url: string;
};

export type EntityType = {
  offset: number;
  length: number;
  data?: EntityTypeData;
  renderer: RendererFn;
};

export type ParserType = (text: string, bodyAttributes?: BodyAttribute[]) => EntityType[];

function renderEmoji(char: string) {
  return <Emoji character={char} />;
}

function renderShortcodeEmoji(code: string) {
  return <ShortcodeEmoji text={code} />;
}

function renderLink(text: string, rendered: JSX.Element, data: EntityTypeData) {
  return (
    <Link href={data.url} target="_blank">
      {rendered}
    </Link>
  );
}

function renderNewline() {
  return <br />;
}

function renderStrong(text: string, rendered: JSX.Element) {
  return <strong>{rendered}</strong>;
}

function renderWhitespace(text: string) {
  return text.replace(/\t/g, '\u00a0\u00a0').replace(/ /g, '\u00a0');
}

/**
 * @param  {RegExp} regExp   without any capture groups (2nd arg of replace fn must be index)
 * @param  {function} filterFn whether the match qualifies for an entity replacement
 * @param  {function} renderer to render in place of the matching text
 * @return {Entities[]}
 */
function createParser(regExp: RegExp, filterFn: Function | null, renderer: RendererFn): ParserType {
  return (text: string) => {
    const entities: EntityType[] = [];
    if (text) {
      text.replace(regExp, (match: string, index) => {
        if (!filterFn || filterFn(match)) {
          entities.push({
            offset: index,
            length: match.length,
            renderer,
          });
        }
        return match;
      });
    }
    return entities;
  };
}

function createUnicodeEmojiParser(): ParserType {
  // like createParser but use the standard emoji replacer,
  // since we don't have access to the regexp it uses.
  return (text: string) => {
    const entities: EntityType[] = [];
    emojiReplace(text, (match: string, index: number) => {
      entities.push({
        offset: index,
        length: match.length,
        renderer: renderEmoji,
      });
    });
    return entities;
  };
}

function createShortcodeEmojiParser(): ParserType {
  const keys = matchStrings(Object.keys(emojiAnnotationMap));
  const regExp = new RegExp(`:(?:${keys}):`, 'g');
  return createParser(regExp, null, renderShortcodeEmoji);
}

function getTrackedLinkAttributes(attributes: BodyAttribute[]): TrackedLinkAttribute[] {
  return attributes.filter(attrib => attrib.type === 'tracking_link') as TrackedLinkAttribute[];
}

const parseUrls: ParserType = (text: string, attributes?: BodyAttribute[]): EntityType[] => {
  // Nothing to parse if there's no text
  if (!text) {
    return [];
  }

  const trackedEntities = getTrackedLinkAttributes(attributes || []).reduce(
    (acc: EntityType[], link) => {
      const lastOffset = acc.length > 0 ? acc[acc.length - 1].offset : -1;
      const index = text.indexOf(link.original_url, lastOffset);
      if (index > lastOffset) {
        return acc.concat({
          offset: index,
          length: link.original_url.length,
          data: { url: link.url },
          renderer: renderLink,
        });
      }
      return acc;
    },
    []
  );

  const urlEntities: EntityType[] = (linkify.match(text) || []).map(match => ({
    offset: match.index,
    length: match.text.length,
    data: { url: match.url },
    renderer: renderLink,
  }));

  // Merge arrays and filter out overlapping entities
  return ([] as EntityType[])
    .concat(trackedEntities, urlEntities)
    .reduce((acc: EntityType[], entity: EntityType) => {
      const overlapsExisting = acc.some((existingEntity: EntityType) => {
        const newEntityOffsetWithinExistingEntity =
          entity.offset >= existingEntity.offset &&
          entity.offset <= existingEntity.offset + existingEntity.length;
        const existingEntityOffsetWithinNewEntity =
          existingEntity.offset >= entity.offset &&
          existingEntity.offset <= entity.offset + entity.length;
        return newEntityOffsetWithinExistingEntity || existingEntityOffsetWithinNewEntity;
      });
      if (!overlapsExisting) {
        return acc.concat(entity);
      } else {
        return acc;
      }
    }, []);
};

export default {
  parseShortcodeEmoji: createShortcodeEmojiParser(),
  parseNewlines: createParser(/\n/g, null, renderNewline),
  parseLeadingWhitespace: createParser(/^[\t ]+/gm, null, renderWhitespace),
  parseUrls,
  parseAttributes(attributes: BodyAttribute[], renderLinkTag: RendererFn): EntityType[] {
    return attributes.reduce((result: EntityType[], item) => {
      switch (item.type) {
        case 'link':
          const linkAttrib = item as LinkAttribute;
          result.push({
            offset: linkAttrib.offset,
            length: linkAttrib.length,
            renderer: renderLinkTag,
            data: { ...linkAttrib, ...linkAttrib.data } as any,
          });
          break;
        default:
          break;
      }
      return result;
    }, []);
  },
  createTextMatchingParser(text: string): ParserType {
    const replacedText = text.replace(/[|\\{}()[\]^$+*?.]/g, '\\$&');
    return createParser(new RegExp(replacedText, 'ig'), null, renderStrong);
  },
  parseEmoji: createUnicodeEmojiParser(),
};
